/**
 * InviteLinkContent - Shared component for generating and sharing deep links.
 * Used by GroupInvitationModal (group invites) and InviteFriendsModal (friend invites).
 * When linkState is passed (controlled), parent owns state and triggers generate only when entering the tab.
 * When linkState is not passed (uncontrolled), generates on mount (e.g. Storybook).
 */

import { useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Link, Copy, Share2 } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useShareLink } from '../../../src/shared/hooks/useShareLink';
import { getInviteLinkContentStyles } from './InviteLinkContent.styles';
import type { DeepLinkType } from '../../../src/shared/types/deepLink.types';

export interface InviteLinkState {
  url: string | null;
  loading: boolean;
  error: string | null;
  generateLink: () => Promise<void>;
  shareLink: () => Promise<void>;
  copyLink: () => Promise<void>;
}

interface InviteLinkContentProps {
  type: DeepLinkType;
  /** Required for group/challenge. Optional for friend (auto-created). */
  targetId?: string;
  description?: string;
  /** When set, use this state (parent controls when to generate). Omit to use internal hook and generate on mount. */
  linkState?: InviteLinkState;
}

export function InviteLinkContent({ type, targetId, description, linkState: controlledState }: InviteLinkContentProps) {
  const theme = useTheme();
  const styles = useMemo(() => getInviteLinkContentStyles(theme), [theme]);
  const internal = useShareLink({ type, targetId });

  const { url, loading, error, generateLink, shareLink, copyLink } = controlledState ?? internal;

  // Uncontrolled mode only: generate on mount (e.g. Storybook). Controlled mode: parent triggers generate when tab is first entered.
  useEffect(() => {
    if (!controlledState) generateLink();
  }, [controlledState, generateLink]);

  const defaultDescription = type === 'group'
    ? 'Share this link to invite people to your group'
    : 'Share this link to invite someone as a friend';

  // Loading state
  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={theme.primary} />
        <Text style={styles.description}>Generating link...</Text>
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity onPress={generateLink} style={styles.retryButton}>
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Link generated
  if (url) {
    return (
      <View style={styles.container}>
        <View style={styles.iconCircle}>
          <Link size={28} color={theme.primary} />
        </View>
        <Text style={styles.description}>{description || defaultDescription}</Text>

        <TouchableOpacity onPress={copyLink} style={styles.urlContainer} activeOpacity={0.7}>
          <Text style={styles.urlText} numberOfLines={1} ellipsizeMode="middle">
            {url}
          </Text>
          <Copy size={18} color={theme.gray[500]} />
        </TouchableOpacity>

        <TouchableOpacity onPress={shareLink} style={styles.shareButtonFull} activeOpacity={0.8}>
          <Share2 size={18} color={theme.onPrimary} />
          <Text style={styles.shareButtonFullText}>Share</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Fallback: generate button (shouldn't reach here with auto-generate)
  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={generateLink} style={styles.generateButton} activeOpacity={0.8}>
        <Text style={styles.generateButtonText}>Generate Link</Text>
      </TouchableOpacity>
    </View>
  );
}
